import { ReactElement, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, Grid, Typography, Link, IconButton, Collapse, Box } from '@mui/material';
import { CITokensUsageChart, FeatureUnavailableBox, GridItemBreakpoints, LoadingFragment } from '@localstack/ui';
import { CloudPodsService, ExtensionService, ComputeService, useApiEffect, useApiGetter } from '@localstack/services';
import { KeyboardArrowDown as ArrowDownIcon, KeyboardArrowUp as ArrowUpIcon } from '@mui/icons-material';

import {
  LocalStackInstance,
  LocalStackInstanceType,
  ComputeUsageSummary,
  UsageSummary,
  ProductFeature,
  PlanFamily,
  EmulatorType,
} from '@localstack/types';

import { DEFAULT_INSTANCE_IMAGE, DEFAULT_INSTANCE_SHAPE, ExternalLink } from '@localstack/constants';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { pricingRoute } from '~/util/pricing';

import { InstanceCard } from './InstanceCard';

const EPH_INST_ID_PREFIX = 'eph_instance_';

const NEW_EPHEMERAL_INSTANCE: LocalStackInstance = {
  id: EPH_INST_ID_PREFIX,
  name: 'New-instance',
  image: 'localstack/localstack-pro',
  auto_load_pod: undefined,
  endpoint: '',
  description: 'Preview instance',
  instanceType: LocalStackInstanceType.EPHEMERAL_INSTANCE,
  startedAt: new Date().getTime(),
  ephemeralInstanceConfig: {
    instance_name: '',
    pod_on_shutdown: true,
    allow_workspace_access: false,
    shape: DEFAULT_INSTANCE_SHAPE,
    env_vars: {},
    image: DEFAULT_INSTANCE_IMAGE,
    labels: {},
    lifetime: undefined,
    requestor: '',
  },
};

function mapComputeUsageSummaryToUsageSummary(computeUsageSummary: ComputeUsageSummary) {
  const { per_day } = computeUsageSummary.usage;

  const sortedKeys = Object.keys(per_day).sort();

  const usagePeriodStart = new Date(sortedKeys[0] ?? '0').getTime() / 1000;
  const usagePeriodEnd = new Date(sortedKeys[sortedKeys.length - 1] ?? '0').getTime() / 1000;

  const usageSummary: UsageSummary = {
    ci_keys_usage_by_date: {},
    usage_by_product: {},
    usage_by_type: {},
    usage_period_end: usagePeriodEnd,
    usage_period_start: usagePeriodStart,
  };

  for (const date in per_day) {
    const instanceUsageRecord = per_day[date];
    if (!usageSummary.ci_keys_usage_by_date) {
      usageSummary.ci_keys_usage_by_date = {};
    }
    usageSummary.ci_keys_usage_by_date[date] = {
      subscription_usage: {
        st: instanceUsageRecord?.compute_credits ?? 0,
      },
    };
  }

  return usageSummary;
}

export const InstanceEphemeral = (): ReactElement => {
  const { hasFeature } = useAuthProvider();

  const hasAccessToEphemeralInstances = hasFeature(ProductFeature.FEATURE_COMPUTE);
  const hasAccessToPods = hasFeature(ProductFeature.FEATURE_PODS);

  const { data: publishedExtensions } = useApiGetter(ExtensionService, 'listExtensionsMarketplace', [], {
    defaultValue: [],
    suppressErrors: true,
  });

  const filteredExtensions = useMemo(
    () => publishedExtensions?.filter((extension) => extension.published),
    [publishedExtensions],
  );

  const extensions = useMemo(
    () =>
      [...(filteredExtensions ?? [])]?.sort((a, b) => (a.display_name ?? 'a')?.localeCompare(b.display_name ?? 'b')) ??
      [],
    [filteredExtensions],
  );

  const { data: computeUsage, isLoading: isLoadingComputeUsage } = useApiGetter(ComputeService, 'getComputeUsage', [], {
    enable: hasAccessToEphemeralInstances,
  });

  const mapped = computeUsage && mapComputeUsageSummaryToUsageSummary(computeUsage);
  const gridItemBreakpoints: GridItemBreakpoints = { xs: 12 };

  const [isCollapsed, setIsCollapsed] = useState(true);

  const { data: allPods } = useApiGetter(CloudPodsService, 'listCloudPods', [], {
    defaultValue: [],
    enable: hasAccessToPods,
  });

  const { createEphemeralInstance, isLoading: isCreatingInstance } = useApiEffect(
    ComputeService,
    ['createEphemeralInstance'],
    { revalidate: ['listEphemeralInstances'] },
  );

  const onCreate = async (ins: LocalStackInstance) => {
    const instance = await createEphemeralInstance({
      ...ins.ephemeralInstanceConfig,
      instance_name: ins.name,
      requestor: ins.ephemeralInstance?.requestor ?? '',
    });
    return { type: LocalStackInstanceType.EPHEMERAL_INSTANCE as const, instance };
  };

  if (!hasAccessToEphemeralInstances) {
    return (
      <Box flexGrow={1} height="100%">
        <FeatureUnavailableBox
          icon="ephemeralInstances"
          availableIn={{ family: PlanFamily.BASE_PLANS, emulators: [EmulatorType.AWS] }}
          upgradeLink={pricingRoute([EmulatorType.AWS])}
          docsLink={ExternalLink.DOCS_EPHEMERAL_INSTANCES}
          title="Ephemeral Instances"
          description="Ephemeral Instances allows you to run a LocalStack instance in the cloud. You can interact with these instances via the LocalStack Console, or by configuring your integrations and developer tools with the endpoint URL of the ephemeral instance."
        />
      </Box>
    );
  }

  return (
    <>
      <Grid item xs={12}>
        <Card>
          <CardHeader title="Ephemeral Instances" titleTypographyProps={{ style: { fontWeight: 'bold' } }} />
          <CardContent>
            <Typography variant="body1">
              Instead of running LocalStack on your machine locally, you can start an ephemeral instance in the cloud.{' '}
              <br />
              These instances can be accessed via our UI or through an automatically generated endpoint URL. <br />
              Deploy your application to the instance to share a preview state, collaborate with others, or hand it off
              to QA. <br />
              For more details on ephemeral instances, please refer to our{' '}
              <Link href={ExternalLink.DOCS_EPHEMERAL_INSTANCES} target="_blank" underline="hover">
                documentation
              </Link>
              .
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <LoadingFragment
        variant="grid"
        size={1}
        loading={hasAccessToEphemeralInstances && isLoadingComputeUsage}
        gridItemBreakpoints={gridItemBreakpoints}
        height={189}
      >
        <Grid item xs={12}>
          {!isLoadingComputeUsage && (
            <InstanceCard
              instance={NEW_EPHEMERAL_INSTANCE}
              onSave={onCreate}
              isSaving={isCreatingInstance}
              pods={allPods ?? []}
              disabled={!hasAccessToEphemeralInstances}
              computeUsage={computeUsage || undefined}
              extensions={extensions ?? []}
              podsEnabled={hasAccessToPods}
            />
          )}
        </Grid>
        {mapped && Object.keys(mapped?.ci_keys_usage_by_date ?? {}).length !== 0 && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Credit consumption"
                titleTypographyProps={{ style: { fontWeight: 'bold' } }}
                action={
                  <IconButton size="small" onClick={() => setIsCollapsed(!isCollapsed)}>
                    {isCollapsed ? <ArrowDownIcon /> : <ArrowUpIcon />}
                  </IconButton>
                }
              />
              <Collapse in={!isCollapsed}>
                <CITokensUsageChart
                  data={mapped}
                  creditRangePercentages={{
                    green: [0, 70],
                    orange: [70, 90],
                    red: [90, 100],
                    extra: [100, 200],
                  }}
                  creditsQuota={computeUsage?.policy.quota.compute_credits ?? 0}
                  subscriptionId="st"
                  leftLabel="Daily credit consumption"
                  rightLabel="Total credit consumption"
                />
              </Collapse>
            </Card>
          </Grid>
        )}
      </LoadingFragment>
    </>
  );
};
